/**
 * URL and colour checks for authored popup / overlay content that is written into HTML.
 *
 * - 'media' (images, videos, PDFs, 3D models): anything except script-running schemes.
 *   Local media arrives as whatever the host resolves it to (Tauri's `asset://localhost/…`
 *   on macOS / Linux, `http://asset.localhost/…` on Windows, `tauri://` / `file://` for
 *   relative paths, `blob:` / `data:` in the editor), and none of those may be dropped.
 * - 'link' (link buttons): web pages plus mail and phone links.
 * - 'web' (embedded iframes): web pages only.
 */
export type UrlPolicy = 'media' | 'link' | 'web';

const SCRIPT_PROTOCOLS = new Set(['javascript:', 'vbscript:']);
const LINK_PROTOCOLS = new Set(['https:', 'http:', 'mailto:', 'tel:']);
const WEB_PROTOCOLS = new Set(['https:', 'http:']);

/** Escape a string for safe insertion into innerHTML (text or a quoted attribute value). */
export function escapeHtml(s: string | undefined | null): string {
    return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/** The resolved URL when `url` is allowed by `policy`, otherwise null. */
export function safeUrl(url: unknown, policy: UrlPolicy): string | null {
    if (typeof url !== 'string' || !url.trim()) return null;
    let parsed: URL;
    try {
        parsed = new URL(url.trim(), typeof location !== 'undefined' ? location.href : 'https://storysplat.com');
    } catch {
        return null;
    }
    const protocol = parsed.protocol.toLowerCase();
    const allowed = policy === 'media' ? !SCRIPT_PROTOCOLS.has(protocol)
        : policy === 'link' ? LINK_PROTOCOLS.has(protocol)
            : WEB_PROTOCOLS.has(protocol);
    return allowed ? parsed.href : null;
}

/** An authored URL as an attribute value: policy-checked, then HTML-escaped; '' when unsafe. */
export function attrUrl(url: unknown, policy: UrlPolicy): string {
    const href = safeUrl(url, policy);
    return href ? escapeHtml(href) : '';
}

/** A CSS color from authored data, or the fallback when it could close the style attribute. */
export function safeCssColor(value: unknown, fallback: string): string {
    return typeof value === 'string' && /^(#[0-9a-fA-F]{3,8}|[a-zA-Z]{1,30}|(rgb|rgba|hsl|hsla)\([\d\s.,%]{1,40}\))$/.test(value.trim()) ? value.trim() : fallback;
}
